"use strict";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Buttons, MenuButton, Tag } from "capitalrx-components-2.0/lib/general";
import * as Color from "capitalrx-components-2.0/lib/general/Color";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Bold, Heading, Paragraph, Regular, Semibold } from "capitalrx-components-2.0/lib/typography";
import classNames from "classnames";
import { useAuthToken, useDataChannel } from "../../hooks";
import styles from "./Notifications.module.css";
const NotificationButtonAppearance = {
  ...Buttons.Functional.Tertiary.Large,
  padding: {
    horizontal: 8,
    vertical: 10
  },
  normal: {
    ...Buttons.Functional.Tertiary.Large.normal,
    foregroundColor: Color.White,
    borderColor: Color.Transparent
  },
  hover: {
    ...Buttons.Functional.Tertiary.Large.hover,
    foregroundColor: Color.Turqoise["100"],
    borderColor: Color.Transparent
  },
  pressed: {
    ...Buttons.Functional.Tertiary.Large.pressed,
    foregroundColor: Color.White,
    borderColor: Color.Transparent
  }
};
export const Notifications = () => {
  const appNotifications = useDataChannel(null, "global-notifications");
  const [authToken] = useAuthToken();
  const [notifications, setNotifications] = useState([]);
  const notificationProviders = appNotifications.map((sub) => sub.data).filter(Boolean);
  const reloadNotificationsList = useCallback(() => {
    if (notificationProviders.length === 0 || !authToken) {
      setNotifications([]);
      return;
    }
    Promise.all(notificationProviders.map((provider) => provider.getNotifications(authToken))).then((results) => {
      const merged = [].concat(...results);
      const deduped = Array.from(new Map(merged.map((n) => [n.id, n])).values());
      setNotifications(deduped);
    }).catch(() => {
    });
  }, [notificationProviders, authToken]);
  useEffect(() => {
    window.reloadNotificationsList = reloadNotificationsList;
    reloadNotificationsList();
    return () => {
      if (window.reloadNotificationsList === reloadNotificationsList) {
        window.reloadNotificationsList = void 0;
      }
    };
  }, [authToken, notificationProviders.length]);
  const markAsRead = (id) => {
    setNotifications((current) => current.map((item) => item.id === id ? { ...item, read: true } : item));
  };
  const markAsReadHandler = useCallback(
    (notification) => {
      if (!authToken) return;
      const provider = notificationProviders[0];
      try {
        provider == null ? void 0 : provider.markRead(authToken, [notification.id], notification);
      } finally {
        markAsRead(notification.id);
      }
    },
    [notificationProviders, authToken]
  );
  const openNotificationHandler = useCallback(
    async (notification) => {
      if (!authToken) return;
      const provider = notificationProviders[0];
      try {
        if (!notification.critical) markAsRead(notification.id);
        await (provider == null ? void 0 : provider.openNotification(authToken, notification.id));
      } catch {
      }
    },
    [notificationProviders, authToken]
  );
  const viewAllNotificationHandler = useCallback(async () => {
    if (!authToken) return;
    const provider = notificationProviders[0];
    try {
      await (provider == null ? void 0 : provider.viewAllNotification(authToken));
    } catch {
    }
  }, [notificationProviders, authToken]);
  const unreadCount = useMemo(() => {
    return notifications.filter((notification) => !notification.read).length;
  }, [notifications]);
  const notificationItems = useMemo(() => {
    return notifications.map((notification) => {
      const { id } = notification;
      return {
        notification: {
          ...notification
        },
        markAsRead: (ev) => {
          if (authToken) {
            const provider = notificationProviders[0];
            try {
              provider == null ? void 0 : provider.markRead(authToken, [id], notification);
            } catch {
            } finally {
              setNotifications((current) => {
                return current.filter((notification2) => notification2.id !== id);
              });
            }
          }
          ev.preventDefault();
        }
      };
    });
  }, [notifications, notificationProviders, authToken]);
  const getTruncatedDescription = useCallback((description, critical, revoked) => {
    const shouldTruncate = critical || !critical && !revoked;
    if (!shouldTruncate) return description;
    return (description == null ? void 0 : description.length) > 200 ? (description == null ? void 0 : description.slice(0, 197)) + "..." : description;
  }, []);
  if (notificationProviders.length === 0) return null;
  const supportEmail = useMemo(() => {
    const primeRegex = /(.?)prime(?:uat|qa)?-cap-rx\.com(.?)/;
    if (primeRegex.test(location.hostname)) {
      return "primesupport@cap-rx.com";
    } else {
      return "support@cap-rx.com";
    }
  }, []);
  return /* @__PURE__ */ jsx("div", { className: "notifications", children: /* @__PURE__ */ jsxs("div", { className: styles.notificationButtonContainer, children: [
    unreadCount > 0 && /* @__PURE__ */ jsx("div", { className: styles.notificationIndicator, children: /* @__PURE__ */ jsx(Bold, { children: unreadCount }) }),
    /* @__PURE__ */ jsx(MenuButton, { appearance: NotificationButtonAppearance, tabIndex: 0, icon: "bell", caretIcon: false, tooltip: "Notifications", children: /* @__PURE__ */ jsx(MenuButton.ComponentItem, { children: /* @__PURE__ */ jsx("div", { className: styles.notificationMenuBody, children: /* @__PURE__ */ jsxs(Stack, { spacing: "medium", children: [
      /* @__PURE__ */ jsxs(Shelf, { style: { height: "24px" }, children: [
        /* @__PURE__ */ jsx(Heading, { children: "Notifications" }),
        /* @__PURE__ */ jsx(Shelf.Spacer, {}),
        notifications.length > 0 && /* @__PURE__ */ jsx(Button, { appearance: "text", onClick: viewAllNotificationHandler, children: "View All" })
      ] }),
      notifications.length === 0 && /* @__PURE__ */ jsx("div", { className: styles.readNotificationsMessage, children: "No new notifications" }),
      notifications.length > 0 && /* @__PURE__ */ jsx("div", { className: styles.notificationScrollAreaOuter, children: /* @__PURE__ */ jsx("div", { className: styles.notificationScrollAreaInner, children: /* @__PURE__ */ jsx(Stack, { spacing: "small", children: notificationItems.map(({ notification }, index) => {
        const { title, description, critical, read, id, revoked, timestamp } = notification;
        const position = (idx) => {
          const lastItem = idx === notificationItems.length - 1;
          if (!lastItem && idx > 0) {
            return "";
          }
          return !lastItem ? "firstItem" : "lastItem";
        };
        const truncatedDescription = getTruncatedDescription(description, critical, revoked);
        return /* @__PURE__ */ jsx("div", { className: classNames(styles.notificationBody, styles[position(index)] || ""), children: /* @__PURE__ */ jsxs(Stack, { spacing: "mini", className: styles.notificationMessage, children: [
          /* @__PURE__ */ jsx(Shelf, { alignment: "center", className: styles.notificationMessageHeader, children: !read ? /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(Body, { size: "mini", children: /* @__PURE__ */ jsx(Semibold, { children: timestamp }) }),
            /* @__PURE__ */ jsx("div", { className: styles.notificationUnreadIndicator })
          ] }) : /* @__PURE__ */ jsx(Body, { size: "mini", children: timestamp }) }),
          critical && !revoked && /* @__PURE__ */ jsxs(Shelf, { spacing: "mini", alignment: "center", style: { justifyContent: "flex-start" }, children: [
            /* @__PURE__ */ jsx(Tag, { type: "error-high-contrast", children: "Critical" }),
            !read && /* @__PURE__ */ jsx(Tag, { type: "system-high-contrast", children: "Acknowledgement Required" })
          ] }),
          revoked ? /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(Shelf, { spacing: "mini", alignment: "center", style: { justifyContent: "flex-start" }, children: /* @__PURE__ */ jsx(Tag, { children: "Revoked" }) }),
            /* @__PURE__ */ jsx(Stack, { children: /* @__PURE__ */ jsx(Semibold, { children: "Revoked Notification Warning" }) }),
            /* @__PURE__ */ jsxs(Stack, { spacing: "mini", children: [
              /* @__PURE__ */ jsxs(Regular, { children: [
                "A notification you previously received titled ",
                /* @__PURE__ */ jsx(Semibold, { children: notification == null ? void 0 : notification.title }),
                " has been revoked and no action is required on your behalf. For questions reach out to",
                " ",
                /* @__PURE__ */ jsx("a", { href: `mailto:${supportEmail}`, children: supportEmail }),
                "."
              ] }),
              /* @__PURE__ */ jsxs(Shelf, { spacing: "mini", children: [
                /* @__PURE__ */ jsx(Semibold, { children: "Reason:" }),
                /* @__PURE__ */ jsx(Regular, { children: description }),
                /* @__PURE__ */ jsx(Shelf.Spacer, {})
              ] })
            ] })
          ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(Semibold, { children: title }),
            /* @__PURE__ */ jsx(Paragraph, { children: truncatedDescription })
          ] }),
          /* @__PURE__ */ jsxs(Shelf, { spacing: "small", children: [
            !read && !revoked && !critical && /* @__PURE__ */ jsx(Shelf, { spacing: "mini", alignment: "center", style: { justifyContent: "flex-start" }, children: /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                className: styles.notificationAction,
                onClick: () => markAsReadHandler(notification),
                children: "Mark As Read"
              }
            ) }),
            /* @__PURE__ */ jsx(Shelf, { spacing: "mini", alignment: "center", style: { justifyContent: "flex-start" }, children: /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                className: styles.notificationAction,
                onClick: () => openNotificationHandler(notification),
                children: "Open"
              }
            ) }),
            /* @__PURE__ */ jsx(Shelf.Spacer, {})
          ] })
        ] }) }, id);
      }) }) }) })
    ] }) }) }) })
  ] }) });
};
